import type React from "react";
import { useState } from "react";

import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Imaging } from "@/features/imagings/schemas/imagings";
import { deleteImaging } from "@/features/imagings/services/imagingService";

/**
 * Props para el componente DeleteStudyDialog
 * @interface DeleteStudyDialogProps
 * @property {boolean} isOpen - Indica si el diálogo está abierto
 * @property {Function} onClose - Función para cerrar el diálogo
 * @property {Imaging} imaging - Estudio de imagen a eliminar
 * @property {Function} onSuccess - Función callback cuando la eliminación es exitosa
 */
interface DeleteStudyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  imaging: Imaging;
  onSuccess?: () => void;
}

/**
 * Componente para confirmar y eliminar un estudio de imagen
 *
 * Este componente proporciona una interfaz segura para eliminar estudios de imagen,
 * incluyendo confirmación visual, detalles del estudio y manejo de estados de carga.
 *
 * Características:
 * - Confirmación visual con detalles del estudio
 * - Estados de carga durante la eliminación
 * - Manejo de errores con notificaciones toast
 * - Prevención de cierre accidental durante la eliminación
 * - Integración con el servicio de eliminación existente
 *
 * Ejemplo de uso:
 * ```tsx
 * const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
 *
 * <DeleteStudyDialog
 *   isOpen={isDeleteDialogOpen}
 *   onClose={() => setIsDeleteDialogOpen(false)}
 *   imaging={selectedImaging}
 *   onSuccess={() => {
 *     // Refrescar lista de estudios
 *     queryClient.invalidateQueries({ queryKey: imagingKeys.all });
 *   }}
 * />
 * ```
 *
 * @param {DeleteStudyDialogProps} props - Propiedades del componente
 * @returns {JSX.Element} - Elemento JSX
 */
export function DeleteStudyDialog({
  isOpen,
  onClose,
  imaging,
  onSuccess,
}: DeleteStudyDialogProps): React.JSX.Element {
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Maneja la confirmación y eliminación del estudio
   */
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);

    try {
      await deleteImaging(imaging.iuid);

      toast.success("Estudio eliminado correctamente", {
        description: `El estudio ${imaging.accession_number} ha sido eliminado permanentemente.`,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error al eliminar estudio:", error);

      toast.error("Error al eliminar estudio", {
        description:
          error instanceof Error
            ? error.message
            : "Ocurrió un error inesperado al eliminar el estudio.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Maneja el cierre del diálogo
   */
  const handleClose = (): void => {
    if (!isDeleting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md p-6">
        <DialogHeader className="flex flex-col items-center gap-4 space-y-0 sm:text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-destructive/20 bg-destructive/5 text-destructive">
            <AlertTriangle className="h-8 w-8" strokeWidth={1.5} />
          </div>
          <DialogTitle className="text-xl font-semibold">
            Confirmar Eliminación
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            ¿Está seguro que desea eliminar el estudio? Esta acción es irreversible.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-xl border bg-slate-50/50 p-5 px-6 border-l-4 border-l-destructive shadow-sm dark:bg-slate-900/50">
            <h4 className="font-semibold text-foreground text-lg mb-1 leading-tight">
              {imaging.patient_name}
            </h4>
            <div className="flex flex-col gap-1 text-sm text-muted-foreground mt-2">
              <span>Modalidad: {imaging.modality}</span>
              <span>Orden: {imaging.accession_number}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3 sm:justify-center flex-row">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            {isDeleting ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Eliminando...
              </>
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
